import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';

import { DropdownMenu, Icon, IconButton, tokens } from '@hotjar/hotsauce';
import type { Highlight } from '@hotjar/webapp/domains/highlights/model';
import type { UIPosition } from '@hotjar/webapp/domains/highlights/api';
import { useSetHighlightSolved } from './SingleHighlight/use-set-highlight-solved';
import { DeleteModal } from './SingleHighlight/DeleteModal';
import { useDeleteModal } from './use-delete-modal';

const ColoredText = styled.span<{ color?: React.ComponentProps<typeof Icon>['color'] }>`
  ${({ color }) =>
    color && color !== 'inherited' && tokens.ALIAS_TEXT_COLORS[color]
      ? `color: ${tokens.ALIAS_TEXT_COLORS[color]};`
      : ''}
`;

const ActionIcon = styled(Icon)`
  margin-right: ${tokens.spacer.x1};
`;

interface HighlightActionDropdownProps {
  authorIsCurrentUser: boolean;
  uiPosition: UIPosition | undefined;
  highlight: Highlight;
  onCloseDeleteModal: () => void;
  siteId: string;
}

export const HighlightActionDropdown = ({
  authorIsCurrentUser,
  uiPosition,
  highlight,
  onCloseDeleteModal,
  siteId,
}: HighlightActionDropdownProps): JSX.Element | null => {
  const { t } = useTranslation();
  const { setHighlightSolved } = useSetHighlightSolved(siteId);
  const { isOpen, openModal, onModalClose } = useDeleteModal(onCloseDeleteModal);

  return (
    <>
      <DropdownMenu
        trigger={
          <IconButton
            description={t('highlights:single.actions')}
            icon="menu_kebab"
            kind="ghost"
            size="s"
            tooltipText={t('collections:actions.description')}
          />
        }
        appendTo="parent"
      >
        <DropdownMenu.BaseItem
          onClick={() => setHighlightSolved(highlight.id, !highlight.solved, uiPosition)}
        >
          <ActionIcon
            icon={highlight.solved ? 'check_alt' : 'check'}
            color={highlight.solved ? 'text_success' : 'text_default'}
            size="m"
          />
          <ColoredText color="text_default">
            {highlight.solved
              ? t('highlights:single.unmark_solved')
              : t('highlights:single.mark_solved')}
          </ColoredText>
        </DropdownMenu.BaseItem>
        {authorIsCurrentUser ? (
          <DropdownMenu.BaseItem
            aria-label={t('highlights:single.remove.description')}
            onClick={() => openModal()}
            hasDivider
          >
            <ActionIcon icon="trash" color="text_warning" size="m" />
            <ColoredText color="text_warning">{t('highlights:single.remove.label')}</ColoredText>
          </DropdownMenu.BaseItem>
        ) : (
          // eslint-disable-next-line react/jsx-no-useless-fragment
          <></>
        )}
      </DropdownMenu>
      <DeleteModal
        siteId={siteId}
        highlightId={highlight.id}
        onModalClose={onModalClose}
        isOpen={isOpen}
        uiPosition={uiPosition}
      />
    </>
  );
};
